import { Injectable, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

@Injectable({
    providedIn: 'root'
})
export class SeoService {

    constructor(
        private titleService: Title,
        private metaService: Meta,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        @Inject(DOCUMENT) private doc: Document
    ) { }

    public init() {
        // Add structured data on initialization
        this.addOrganizationStructuredData();
        this.addWebsiteStructuredData();
        this.addSoftwareApplicationStructuredData();

        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => this.activatedRoute),
            map((route) => {
                while (route.firstChild) {
                    route = route.firstChild;
                }
                return route;
            }),
            filter((route) => route.outlet === 'primary'),
            mergeMap((route) => route.data)
        ).subscribe((event) => {
            this.updateTitle(event['title']);
            this.updateDescription(event['description']);
            this.updateOgUrl(this.router.url);
        });
    }

    public updateTitle(title: string) {
        if (title) {
            this.titleService.setTitle(`${title} | Evaltrix`);
            this.metaService.updateTag({ name: 'title', content: `${title} | Evaltrix` });
            this.metaService.updateTag({ property: 'og:title', content: `${title} | Evaltrix` });
            this.metaService.updateTag({ name: 'twitter:title', content: `${title} | Evaltrix` });
        } else {
            this.titleService.setTitle('Evaltrix - AI Powered Assessment Platform');
        }
    }

    public updateDescription(desc: string) {
        if (desc) {
            this.metaService.updateTag({ name: 'description', content: desc });
            this.metaService.updateTag({ property: 'og:description', content: desc });
            this.metaService.updateTag({ name: 'twitter:description', content: desc });
        } else {
            this.metaService.updateTag({ name: 'description', content: 'Evaltrix is a comprehensive assessment platform for hiring, training, and educational purposes.' });
        }
    }

    public updateOgUrl(url: string) {
        this.metaService.updateTag({ property: 'og:url', content: `https://evaltrix.com${url}` });
        this.metaService.updateTag({ name: 'twitter:url', content: `https://evaltrix.com${url}` });
    }

    /**
     * Add Schema.org Organization structured data
     */
    private addOrganizationStructuredData() {
        const script = this.doc.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            'name': 'Evaltrix',
            'url': 'https://evaltrix.com',
            'logo': 'https://evaltrix.com/assets/logo.png',
            'description': 'AI-powered assessment platform for smart hiring and talent evaluation',
            'email': 'support@evaltrix.com',
            'sameAs': [
                'https://www.linkedin.com/company/evaltrix/'
            ],
            'contactPoint': {
                '@type': 'ContactPoint',
                'email': 'support@evaltrix.com',
                'contactType': 'Customer Support',
                'availableLanguage': 'English'
            }
        });
        this.doc.head.appendChild(script);
    }

    /**
     * Add Schema.org WebSite structured data for search functionality
     */
    private addWebsiteStructuredData() {
        const script = this.doc.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            'name': 'Evaltrix',
            'url': 'https://evaltrix.com',
            'description': 'Transform your hiring with AI-powered assessments',
            'potentialAction': {
                '@type': 'SearchAction',
                'target': 'https://help.evaltrix.com/product?search={search_term_string}',
                'query-input': 'required name=search_term_string'
            }
        });
        this.doc.head.appendChild(script);
    }

    /**
     * Add Schema.org SoftwareApplication structured data
     */
    private addSoftwareApplicationStructuredData() {
        const script = this.doc.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            'name': 'Evaltrix',
            'applicationCategory': 'BusinessApplication',
            'operatingSystem': 'Web',
            'url': 'https://evaltrix.com',
            'image': 'https://evaltrix.com/assets/logo.png',
            'offers': {
                '@type': 'Offer',
                'price': '0',
                'priceCurrency': 'USD'
            }
        });
        this.doc.head.appendChild(script);
    }
}
